/**
 * SAT State Management - sessionStorage based state persistence
 */
(function (global) {
  "use strict";

  var KEYS = {
    imageUrls: "satImageUrls",
    scores: "satScores",
    assessment: "satAssessment",
    report: "satReport",
    sessionMeta: "satSessionMeta",
    cartMessages: "satCartMessages",
    // Owned and written by sat-chat.js (SATChatClient), which needs the raw
    // key. It is listed here so clearAll() genuinely clears everything the
    // flow leaves behind - a stale thread reappearing under a fresh report
    // was the reason it had to be removed by hand at each call site.
    chatThread: "satChatThread",
  };

  function get(key) {
    try {
      var data = sessionStorage.getItem(key);
      return data ? JSON.parse(data) : null;
    } catch (e) {
      return null;
    }
  }

  function set(key, value) {
    try {
      if (value === null || value === undefined) {
        sessionStorage.removeItem(key);
      } else {
        sessionStorage.setItem(key, JSON.stringify(value));
      }
    } catch (e) {
      console.error("[SATState] Failed to save:", key, e);
    }
  }

  global.SATState = {
    getImageUrls: function () {
      return get(KEYS.imageUrls);
    },
    setImageUrls: function (data) {
      set(KEYS.imageUrls, data);
    },

    getScores: function () {
      return get(KEYS.scores);
    },
    setScores: function (data) {
      set(KEYS.scores, data);
    },

    getAssessment: function () {
      return get(KEYS.assessment) || {};
    },
    setAssessment: function (data) {
      set(KEYS.assessment, data);
    },

    getReport: function () {
      return get(KEYS.report);
    },
    setReport: function (data) {
      set(KEYS.report, data);
    },

    // Every cart-updated block the session has produced, oldest first, so a
    // refresh brings back the whole run of them rather than only the last.
    getCartMessages: function () {
      var list = get(KEYS.cartMessages);
      return Array.isArray(list) ? list : [];
    },
    addCartMessage: function (data) {
      var list = this.getCartMessages();
      list.push(data);
      set(KEYS.cartMessages, list);
    },

    getSessionMeta: function () {
      return get(KEYS.sessionMeta);
    },
    setSessionMeta: function (data) {
      set(KEYS.sessionMeta, data);
    },

    // The sessionStorage key sat-chat.js reads and writes its thread under.
    KEYS: KEYS,

    /**
     * A new photo starts a new assessment, so everything derived from the old
     * one goes: the upload itself, the scores and questionnaire answers built
     * on it, the report, and the results page's chat thread and cart messages.
     *
     * This matters most for `scores` - sat-analysis.liquid treats a stored
     * score set as "coming back into the flow" and skips the /scores call
     * entirely, so leaving it behind makes a fresh photo reuse the previous
     * test's analysis.
     *
     * The keys are listed rather than swept so that a key added later has to
     * make an explicit decision about surviving a new photo.
     */
    clearForNewPhoto: function () {
      ["imageUrls", "scores", "assessment", "report", "chatThread", "cartMessages"].forEach(
        function (k) {
          sessionStorage.removeItem(KEYS[k]);
        }
      );
    },

    /**
     * A regenerated report replaces the finished one outright, so everything
     * that belonged to it goes with it: the report itself, the results page's
     * chat thread and the cart messages that thread produced.
     *
     * Backing out of results to change an answer and submitting again reaches
     * neither Start over nor Retake, so nothing else on that path clears them
     * and the previous conversation replayed under the new report.
     *
     * Keyed to a report actually being generated rather than to leaving the
     * results page: backing out and returning without regenerating should
     * still find the conversation where it was left.
     */
    clearForNewReport: function () {
      ["report", "chatThread", "cartMessages"].forEach(function (k) {
        sessionStorage.removeItem(KEYS[k]);
      });
    },

    clearAll: function () {
      Object.keys(KEYS).forEach(function (k) {
        sessionStorage.removeItem(KEYS[k]);
      });
    },
  };
})(window);
